import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * "Keep screen awake" control backed by the Wake Lock API (§24.6). Unsupported browsers report
 * `supported: false` and the toggle is a no-op — never an error. The lock is re-acquired when the
 * tab becomes visible again (the OS releases it on tab hide) and released on unmount.
 */

interface WakeLockSentinelLike {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}
interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function getWakeLock(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null;
  const nav = navigator as unknown as { wakeLock?: WakeLockLike };
  return nav.wakeLock ?? null;
}

export interface WakeLockState {
  supported: boolean;
  active: boolean;
  error: string | null;
  toggle: () => void;
}

export function useWakeLock(): WakeLockState {
  const supported = getWakeLock() !== null;
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const wantRef = useRef(false);

  const acquire = useCallback(async () => {
    const wl = getWakeLock();
    if (!wl) return;
    try {
      const sentinel = await wl.request('screen');
      sentinelRef.current = sentinel;
      sentinel.addEventListener('release', () => {
        sentinelRef.current = null;
        setActive(false);
      });
      setActive(true);
      setError(null);
    } catch {
      setError('Could not keep the screen awake.');
      setActive(false);
      wantRef.current = false;
    }
  }, []);

  const release = useCallback(async () => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    setActive(false);
    if (sentinel) {
      try {
        await sentinel.release();
      } catch {
        // Already released.
      }
    }
  }, []);

  const toggle = useCallback(() => {
    if (!supported) return;
    if (wantRef.current) {
      wantRef.current = false;
      void release();
    } else {
      wantRef.current = true;
      void acquire();
    }
  }, [supported, acquire, release]);

  // Re-acquire when returning to a visible tab if the user still wants the lock.
  useEffect(() => {
    if (!supported) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && wantRef.current && !sentinelRef.current) {
        void acquire();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      wantRef.current = false;
      void release();
    };
  }, [supported, acquire, release]);

  return { supported, active, error, toggle };
}
